import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import BarChartIcon from "@mui/icons-material/BarChart";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PreviewIcon from "@mui/icons-material/Preview";
import SaveIcon from "@mui/icons-material/Save";
import SelectAllIcon from "@mui/icons-material/SelectAll";

import api from "../../services/api/axios";
import BrandSpinner from "../../shared/components/BrandSpinner";
import BackButton from "../../shared/components/BackButton";
import { downloadBlobFile } from "../../utils/download";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";

const PAGE_BG = "#f7f9fc";
const CARD_SX = { border: `1.5px solid ${BRAND_BLUE}`, borderRadius: 2, boxShadow: "0 8px 20px rgba(15,23,42,0.08)", background: "#fff", overflow: "hidden" };

const REPORT_TYPES = [
  { value: "member_facing", label: "Member-facing PDF" },
  { value: "internal_staff", label: "Internal staff version" },
  { value: "board_summary", label: "Board summary" },
  { value: "investor_tier", label: "Investor tier report" },
  { value: "renewal_packet", label: "Renewal packet export" },
];

const SECTIONS = [
  ["member_snapshot", "Member Snapshot"],
  ["membership_tier_and_investment", "Membership Tier & Investment"],
  ["events_attended", "Events Attended"],
  ["contacts_who_participated", "Contacts Who Participated"],
  ["sponsorships_and_fulfilled_benefits", "Sponsorships & Fulfilled Benefits"],
  ["estimated_value_received", "Estimated Value Received"],
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function yearAgoIso() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().slice(0, 10);
}

function display(value) {
  if (value == null || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.map(display).join("; ") : "-";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function SectionBlock({ title, value }) {
  return (
    <Box sx={{ borderTop: "1px solid #e5edf6", pt: 2, mt: 2 }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, mb: 1 }}>{title}</Typography>
      {Array.isArray(value) ? (
        value.length ? (
          <Stack spacing={1}>
            {value.map((row, index) => (
              <Box key={index} sx={{ bgcolor: "#f8fafc", borderRadius: 1, p: 1.25 }}>
                <Typography component="pre" sx={{ color: "#334155", fontFamily: "inherit", whiteSpace: "pre-wrap", m: 0, fontSize: 13 }}>{display(row)}</Typography>
              </Box>
            ))}
          </Stack>
        ) : <Typography sx={{ color: "#64748b" }}>No activity recorded.</Typography>
      ) : typeof value === "object" ? (
        <Grid container spacing={1}>
          {Object.entries(value || {}).map(([key, item]) => (
            <Grid item xs={12} md={6} key={key}>
              <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>{key.replaceAll("_", " ")}</Typography>
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 600, whiteSpace: "pre-wrap" }}>{display(item)}</Typography>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography sx={{ color: BRAND_BLUE }}>{display(value)}</Typography>
      )}
    </Box>
  );
}

function ReportPreview({ report }) {
  if (!report) {
    return (
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 760,
          borderRadius: 2,
          gap: 2,
          color: "#8a98b3",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "#eef2f8",
            display: "grid",
            placeItems: "center",
          }}
        >
          <BarChartIcon sx={{ fontSize: 32, color: "#b0bdd4" }} />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              color: "#344863",
              mb: 0.5,
            }}
          >
            No preview yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#8a98b3", maxWidth: 260 }}>
            Configure your report on the left and click Preview Report to see results here.
          </Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 3 }}>
        <Typography sx={{ color: BRAND_BLUE, fontSize: 24, fontWeight: 800 }}>{report.member?.name} ROI Report</Typography>
        <Typography sx={{ color: "#64748b", mt: 0.5 }}>
          {REPORT_TYPES.find((type) => type.value === report.report_type)?.label || report.report_type} | {report.date_range?.start} to {report.date_range?.end}
        </Typography>
        {report.summary ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
            {Object.entries(report.summary).map(([key, value]) => <Chip key={key} label={`${key.replaceAll("_", " ")}: ${display(value)}`} />)}
          </Stack>
        ) : null}
        {Object.entries(report.sections || {}).map(([key, value]) => (
          <SectionBlock key={key} title={key.replaceAll("_", " ")} value={value} />
        ))}
      </CardContent>
    </Card>
  );
}

export default function MembershipReports() {
  const previewRef = useRef(null);
  const [members, setMembers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState("");
  const [form, setForm] = useState({
    member_id: "",
    member_ids: [],
    start: yearAgoIso(),
    end: todayIso(),
    report_type: "member_facing",
    sections: SECTIONS.map(([key]) => key),
    export_member_tier: "",
    export_status: "",
  });

  useEffect(() => {
    Promise.all([
      api.get("/membership/members", { params: { limit: 500, offset: 0, sort_by: "name", sort_dir: "asc" } }),
      api.get("/membership/memberships", { params: { limit: 500, offset: 0 } }),
    ])
      .then(([memberResponse, membershipResponse]) => {
        setMembers(memberResponse.data?.data || []);
        setMemberships(membershipResponse.data?.data || []);
      })
      .catch(() => setError("Failed to load members."))
      .finally(() => setLoadingMembers(false));
  }, []);

  const selectedMember = useMemo(() => members.find((member) => member.id === Number(form.member_id)), [form.member_id, members]);
  const allSectionsSelected = form.sections.length === SECTIONS.length;
  const exportOptions = useMemo(() => {
    const tiers = new Set();
    const statuses = new Set();
    memberships.forEach((membership) => {
      if (membership.membership_level) tiers.add(membership.membership_level);
    });
    members.forEach((member) => {
      if (member.status) statuses.add(member.status);
    });
    return { tiers: [...tiers].sort(), statuses: [...statuses].sort() };
  }, [members, memberships]);

  const setValue = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (key) => () => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.includes(key)
        ? prev.sections.filter((item) => item !== key)
        : [...prev.sections, key],
    }));
  };

  const payload = {
    member_id: Number(form.member_id),
    start: form.start,
    end: form.end,
    sections: form.sections,
    report_type: form.report_type,
  };

  const preview = async () => {
    setPreviewLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.post("/membership/roi-reports/preview", payload);
      setReport(response.data.report);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const generate = async () => {
    setPreviewLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.post("/membership/roi-reports/generate", payload);
      setReport(response.data.report);
      setSuccess(response.data.generated_report_id ? `Report saved to member record as generated report #${response.data.generated_report_id}.` : "Report generated.");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Generate failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const batchGenerate = async () => {
    setPreviewLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.post("/membership/roi-reports/batch", {
        member_ids: form.member_ids.map(Number),
        start: form.start,
        end: form.end,
        sections: form.sections,
        report_type: form.report_type,
        save_to_member: false,
      });
      setSuccess(`Generated ${response.data.generated} batch report records.`);
      setReport(response.data.reports?.[0]?.report || null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Batch generation failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!report) return;
    if (form.member_id) {
      try {
        const response = await api.post("/membership/roi-reports/generate", payload);
        setReport(response.data.report);
        setSuccess(response.data.generated_report_id ? `PDF export audited as generated report #${response.data.generated_report_id}.` : "PDF export generated.");
      } catch {
        // Keep client-side PDF export available even if audit creation fails.
      }
    }
    const { default: jsPDF } = await import("jspdf");
    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 42;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = margin;
    const write = (text, size = 10, bold = false) => {
      pdf.setFont("helvetica", bold ? "bold" : "normal");
      pdf.setFontSize(size);
      const lines = pdf.splitTextToSize(String(text), pageWidth - margin * 2);
      lines.forEach((line) => {
        if (y > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
        pdf.text(line, margin, y);
        y += size + 5;
      });
    };
    write(`${report.member?.name} ROI Report`, 18, true);
    write(`${report.date_range?.start} to ${report.date_range?.end} | ${report.report_type}`, 10);
    Object.entries(report.sections || {}).forEach(([key, value]) => {
      y += 8;
      write(key.replaceAll("_", " "), 13, true);
      write(display(value), 9);
    });
    pdf.save(`${report.member?.name || "member"}_roi_report.pdf`);
  };

  const downloadExport = async (type, filename) => {
    setExporting(type);
    setError("");
    setSuccess("");
    try {
      const params = {
        start: form.start,
        end: form.end,
        member_id: form.member_id || undefined,
        member_tier: form.export_member_tier || undefined,
        status: form.export_status || undefined,
      };
      const response = await api.get(`/membership/exports/${type}`, { params, responseType: "blob" });
      downloadBlobFile(response.data, filename);
      setSuccess("Export downloaded and audit record created.");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Export failed.");
    } finally {
      setExporting("");
    }
  };

  if (loadingMembers) return <BrandSpinner label="Loading report builder..." />;

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 5, borderBottom: "1px solid #dce4ef", background: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, md: 3 }, py: 1.75 }}>
          <BackButton to="/membership" />
          <Box sx={{ width: 32, height: 32, borderRadius: 2, background: BRAND_GREEN, color: "#fff", display: "grid", placeItems: "center" }}>
            <ArticleIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 21, fontWeight: 800, color: BRAND_BLUE }}>Membership ROI Report Builder</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>Preview, export, and save member ROI reports.</Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "380px 1fr" },
          gap: 2,
          px: { xs: 2, md: 3 },
          mt: 3,
          alignItems: "start",
        }}
      >
          <Box>
            <Card sx={CARD_SX}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <TextField select label="Member" value={form.member_id} onChange={setValue("member_id")} fullWidth>
                    {members.map((member) => <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>)}
                  </TextField>
                  <TextField select label="Batch Members" value={form.member_ids} onChange={setValue("member_ids")} fullWidth SelectProps={{ multiple: true }}>
                    {members.map((member) => <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>)}
                  </TextField>
                  <Button startIcon={<SelectAllIcon />} onClick={() => setForm((prev) => ({ ...prev, member_ids: members.map((member) => member.id) }))} sx={SECONDARY_BUTTON_SX}>Select All for Batch</Button>
                  <Grid container spacing={1}>
                    <Grid item xs={6}><TextField label="Start" type="date" value={form.start} onChange={setValue("start")} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                    <Grid item xs={6}><TextField label="End" type="date" value={form.end} onChange={setValue("end")} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                  </Grid>
                  <TextField select label="Report Type" value={form.report_type} onChange={setValue("report_type")} fullWidth>
                    {REPORT_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                  </TextField>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Report Sections</Typography>
                      <Button
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            sections: allSectionsSelected ? [] : SECTIONS.map(([key]) => key),
                          }))
                        }
                        sx={{
                          ml: "auto",
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "none",
                          color: BRAND_BLUE,
                          p: 0,
                          minWidth: 0,
                          "&:hover": {
                            background: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {allSectionsSelected ? "Deselect All" : "Select All"}
                      </Button>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {SECTIONS.map(([key, label]) => {
                        const selected = form.sections.includes(key);
                        return (
                          <Chip
                            key={key}
                            label={label}
                            onClick={toggleSection(key)}
                            sx={{
                              height: 30,
                              fontSize: 12.5,
                              fontWeight: 700,
                              cursor: "pointer",
                              border: "1.5px solid",
                              borderColor: selected ? BRAND_BLUE : "#dce4ef",
                              backgroundColor: selected ? "rgba(0,51,108,0.1)" : "#f8fafc",
                              color: BRAND_BLUE,
                              "& .MuiChip-label": { px: 1.25 },
                              "&:hover": {
                                backgroundColor: selected ? "rgba(0,51,108,0.16)" : "#eef2f8",
                                borderColor: selected ? BRAND_BLUE : "#c8d5e8",
                              },
                              transition: "all 0.15s ease",
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                  <Button startIcon={<PreviewIcon />} onClick={preview} disabled={!form.member_id || previewLoading} sx={PRIMARY_BUTTON_SX}>Preview Report</Button>
                  <Button startIcon={<SaveIcon />} onClick={generate} disabled={!form.member_id || previewLoading} sx={PRIMARY_BUTTON_SX}>Generate Report</Button>
                  <Button startIcon={<PictureAsPdfIcon />} onClick={exportPdf} disabled={!report} sx={SECONDARY_BUTTON_SX}>Generate PDF</Button>
                  <Button onClick={batchGenerate} disabled={!form.member_ids.length || previewLoading} sx={SECONDARY_BUTTON_SX}>Generate Batch Reports</Button>
                  {selectedMember ? <Chip label={`Selected: ${selectedMember.name}`} /> : null}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ ...CARD_SX, mt: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, mb: 2 }}>Membership Exports</Typography>
                <Stack spacing={2}>
                  <TextField select label="Export Tier Filter" value={form.export_member_tier} onChange={setValue("export_member_tier")} fullWidth>
                    <MenuItem value="">All tiers</MenuItem>
                    {exportOptions.tiers.map((tier) => <MenuItem key={tier} value={tier}>{tier}</MenuItem>)}
                  </TextField>
                  <TextField select label="Export Status Filter" value={form.export_status} onChange={setValue("export_status")} fullWidth>
                    <MenuItem value="">All statuses</MenuItem>
                    {exportOptions.statuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                  </TextField>
                  <Button disabled={Boolean(exporting)} onClick={() => downloadExport("members", "membership-member-list.xlsx")} sx={SECONDARY_BUTTON_SX}>Excel Member List</Button>
                  <Button disabled={Boolean(exporting)} onClick={() => downloadExport("attendance", "membership-attendance-report.xlsx")} sx={SECONDARY_BUTTON_SX}>Excel Attendance Report</Button>
                  <Button disabled={Boolean(exporting)} onClick={() => downloadExport("sponsorships", "membership-sponsorship-report.xlsx")} sx={SECONDARY_BUTTON_SX}>Excel Sponsorship Report</Button>
                  <Button disabled={Boolean(exporting)} onClick={() => downloadExport("board-summary", "membership-board-summary.xlsx")} sx={SECONDARY_BUTTON_SX}>Board Summary Export</Button>
                  <Button disabled={Boolean(exporting)} onClick={() => downloadExport("renewal-call-list", "membership-renewal-call-list.xlsx")} sx={SECONDARY_BUTTON_SX}>Renewal Call List Export</Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Stack spacing={2}>
              {previewLoading ? <BrandSpinner compact label="Building report..." py={3} /> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              {success ? <Alert severity="success">{success}</Alert> : null}
              <Box ref={previewRef}>
                <ReportPreview report={report} />
              </Box>
            </Stack>
          </Box>
      </Box>
    </Box>
  );
}
